import {
	ChangeDetectorRef,
	Component,
	ComponentRef,
	ElementRef,
	HostListener,
	Injector,
	Type,
	ViewContainerRef,
	inject,
	viewChild,
	Signal
} from '@angular/core';
import { KeyboardKey } from '@app/shared/ui/components/inputs/inputs.model';
import { map, Subscription } from 'rxjs';
import { CreationDrawerInitialData } from '@app/shared/components/new-drawer/models/new-drawer.model';
import { DrawerService } from '@app/shared/services/drawer/drawer.service';
import { DropDownOption } from '@core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { ContactsSearchFormComponent } from '@app/shared/components/new-drawer/search/components/contacts-search/contacts-search-form.component';
import { EmployeesSearchFormComponent } from '@app/shared/components/new-drawer/search/components/employees-search/employees-search-form.component';
import { RecipientsSearchFormComponent } from '@app/shared/components/new-drawer/search/components/recipients-search/recipients-search-form.component';
import { Router } from '@angular/router';
import {
	SearchDrawerNormalizedData,
	searchDrawerNormalizer
} from '@app/shared/components/new-drawer/search/normalizers/search-drawer.normalizers';
import { SearchOptionsValue } from '@app/shared/components/new-drawer/new-drawer.enums';
import { setComponentInputs } from '@core/utils/component-inputs';

@Component({
	selector: 'search-drawer',
	templateUrl: './search-drawer.component.html',
	styleUrls: ['./search-drawer.component.scss'],
	standalone: false
})
export class SearchDrawerComponent {
	private cdr = inject(ChangeDetectorRef);
	private router = inject(Router);
	private injector = inject(Injector);
	private drawerService = inject(DrawerService);
	private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

	private readonly host: Signal<ViewContainerRef> = viewChild.required('contentHost', { read: ViewContainerRef });
	private activeRef?: ComponentRef<any> = null;
	private outputSubs: Subscription[] = [];
	private initialData: CreationDrawerInitialData = null;
	private formInitialData: Record<string, any> = null;

	public readonly ITEM_CONTROL_NAME: string = 'item';
	public readonly SEARCH_TERMS_CONTROL_NAME: string = 'searchTerms';

	public searchForm: UntypedFormGroup = new UntypedFormGroup({
		[this.ITEM_CONTROL_NAME]: new UntypedFormControl(),
		[this.SEARCH_TERMS_CONTROL_NAME]: new UntypedFormControl()
	});
	public itemOptions: DropDownOption[] = [
		{ label: 'DRAWERS.SEARCH.CONTROLS.ITEM.OPTIONS.EMPLOYEES', value: SearchOptionsValue.Employees },
		{ label: 'DRAWERS.SEARCH.CONTROLS.ITEM.OPTIONS.CONTACTS', value: SearchOptionsValue.Contacts },
		{ label: 'DRAWERS.SEARCH.CONTROLS.ITEM.OPTIONS.RECIPIENTS', value: SearchOptionsValue.Recipients }
	];
	public selectedItem: SearchOptionsValue = null;

	private readonly OPEN_LIST_SELECTOR: string =
		'.container__dropdown-menu--openDown, .container__autocomplete--menu-open';

	@HostListener('window:keydown', ['$event'])
	public onWindowKeydown(event: KeyboardEvent): void {
		if (event.key !== KeyboardKey.Enter) {
			return;
		}
		if (this.elementRef.nativeElement.querySelector(this.OPEN_LIST_SELECTOR)) {
			return;
		}
		event.stopImmediatePropagation();
		this.activeRef?.instance?.onSubmit?.();
	}

	ngOnInit() {
		const itemCtrl: AbstractControl = this.searchForm.get(this.ITEM_CONTROL_NAME)!;

		itemCtrl.valueChanges
			.pipe(
				filter(item => !!item),
				distinctUntilChanged(),
				tap((value: SearchOptionsValue) => {
					this.selectedItem = value;
				}),
				map(item => this.pickComponent(item))
			)
			.subscribe(({ cmp, inputs, outputs }) => {
				this.setActiveComponent(cmp, inputs, outputs);
				this.cdr.detectChanges();
			});

		if (this.initialData) {
			Object.keys(this.initialData).forEach(value => {
				if (this.searchForm.get(value)) {
					this.searchForm.get(value)!.setValue(this.initialData[value], { emitEvent: true });
					this.cdr.detectChanges();
				}
			});
		}
	}

	ngOnDestroy() {
		this.outputSubs.forEach(s => s.unsubscribe());
		this.activeRef?.destroy();
	}

	private resetActiveComponent(): void {
		this.activeRef?.destroy();
		this.host().clear();
	}

	/** Call this whenever your form logic decides a new component */
	private setActiveComponent<T>(
		cmp: Type<T>,
		inputs?: Record<string, unknown>,
		outputs?: Record<string, (value: any) => void>
	): void {
		// clean previous
		this.resetActiveComponent();

		// create
		const ref: ComponentRef<T> = this.host().createComponent<T>(cmp, {
			injector: this.injector
		});

		// apply inputs
		if (inputs) setComponentInputs(ref, inputs);

		// wire outputs (EventEmitter fields)
		if (outputs) {
			Object.entries(outputs).forEach(([key, handler]) => {
				const maybeEmitter = (ref.instance as any)[key];
				if (maybeEmitter?.subscribe) {
					this.outputSubs.push(maybeEmitter.subscribe(handler));
				}
			});
		}

		this.activeRef = ref;
	}

	/** Central mapping for form → component decision */
	private pickComponent(item: SearchOptionsValue): {
		cmp: any;
		inputs?: Record<string, any>;
		outputs?: Record<string, (v: any) => void>;
	} {
		switch (item) {
			case SearchOptionsValue.Employees:
				return {
					cmp: EmployeesSearchFormComponent,
					inputs: { item, formData: this.formInitialData },
					outputs: { submit: (res: any) => this.redirectToRoute(res) }
				};
			case SearchOptionsValue.Contacts:
				return {
					cmp: ContactsSearchFormComponent,
					inputs: { item, formData: this.formInitialData }, // pass what the child needs
					outputs: { submit: (res: any) => this.redirectToRoute(res) }
				};
			case SearchOptionsValue.Recipients:
				return {
					cmp: RecipientsSearchFormComponent,
					inputs: { item, formData: this.formInitialData },
					outputs: { submit: (res: any) => this.redirectToRoute(res) }
				};
		}
	}

	private redirectToRoute(res: Record<string, any>): void {
		const searchTerm: string = this.searchForm.get(this.SEARCH_TERMS_CONTROL_NAME).value;
		const normalizedData: SearchDrawerNormalizedData = searchDrawerNormalizer(this.selectedItem, res, searchTerm);
		this.router.navigate([normalizedData.route], {
			queryParams: normalizedData.searchParams
		});
		this.drawerService.close();
	}
}
